import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/reveal";
import SEO from "@/components/seo";
import { CheckCircle, Heart, ShieldCheck } from "lucide-react";

export default function ActiveRetireeProgram() {
  const requirements = [
    "A reliable vehicle and driver's license",
    "A smartphone and the capability to use our app",
    "The ability to walk 2 miles or more at one time",
    "A background screening at the time of application",
  ];

  return (
    <div className="py-16 md:py-24">
      <SEO
        title="Active Retiree Program | Pet Care Jobs in Tallahassee"
        description="Tallahassee Lassie's Active Retiree Program offers flexible, part-time pet care work with a self-set schedule for retirees who love animals."
        path="/active-retiree-program"
      />
      <div className="container mx-auto max-w-5xl px-4">
        <Reveal>
          <div className="mb-16 text-center">
            <div className="mb-6 inline-flex h-20 w-20 items-center justify-center rounded-full bg-secondary/10 text-secondary">
              <Heart size={40} fill="currentColor" />
            </div>
            <h1 className="mb-6 font-serif text-4xl font-bold text-foreground md:text-6xl">
              Active Retiree Program
            </h1>
            <p className="mx-auto max-w-3xl text-xl leading-relaxed text-muted-foreground">
              Our Active Retiree Program is perfect for retirees who want to
              work with pets on a part-time basis. Employees in this program are
              able to set their own weekly schedule and work as few hours as
              they desire. You may want to work mornings only or only middays;
              it&apos;s up to you!
            </p>
          </div>
        </Reveal>

        <div className="grid items-stretch gap-10 md:grid-cols-2">
          <Reveal direction="left">
            <section
              className="h-full border border-secondary/20 bg-secondary/5 p-8 md:p-10"
              style={{ borderRadius: "32px 0 32px 0" }}
            >
              <div className="mb-6 flex items-center gap-3">
                <Heart className="text-secondary" size={28} />
                <h2 className="font-serif text-3xl font-bold">Work on your terms</h2>
              </div>
              <p className="text-lg leading-relaxed text-foreground/80">
                Choose the weekly schedule that works best for you. Active
                Retiree Program employees can work mornings, middays, or as few
                hours as they desire.
              </p>
            </section>
          </Reveal>

          <Reveal direction="right">
            <section
              className="h-full border border-border bg-card p-8 shadow-sm md:p-10"
              style={{ borderRadius: "0 32px 0 32px" }}
            >
              <div className="mb-6 flex items-center gap-3">
                <ShieldCheck className="text-primary" size={28} />
                <h2 className="font-serif text-3xl font-bold">Program requirements</h2>
              </div>
              <p className="mb-6 text-lg leading-relaxed text-muted-foreground">
                Applicants must meet the same requirements as other employees.
                All employees undergo a background screening at the time of
                application.
              </p>
              <ul className="space-y-4">
                {requirements.map((requirement) => (
                  <li key={requirement} className="flex items-start gap-3">
                    <CheckCircle className="mt-1 shrink-0 text-secondary" size={20} />
                    <span className="text-foreground/80">{requirement}</span>
                  </li>
                ))}
              </ul>
            </section>
          </Reveal>
        </div>

        <Reveal>
          <section
            className="mt-16 bg-primary p-10 text-center text-white md:p-12"
            style={{ borderRadius: "40px 0 40px 0" }}
          >
            <h2 className="mb-5 font-serif text-3xl font-bold">Ready to learn more?</h2>
            <p className="mx-auto mb-8 max-w-2xl text-lg leading-relaxed text-white/90">
              If you&apos;re looking for flexible, part-time work with pets,
              we&apos;d love to hear from you.
            </p>
            <Button asChild size="lg" className="h-14 bg-white px-10 text-lg text-primary hover:bg-white/90">
              <Link href="/join-our-team">
                View Team Opportunities
              </Link>
            </Button>
          </section>
        </Reveal>
      </div>
    </div>
  );
}